import { Component, OnInit, inject } from "@angular/core";
import { DatamanagementDataService } from "@app/datamanagement/services/datamanagement-data.service";
import { FilterComponent } from "@enums/workspace/FilterComponent";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { FormatType } from "@enums/FormatType";
import { RendererType } from "@enums/RendererType";
import { Pages } from "../../../../common/constants/pages/Pages";
import { FilterType } from "../../../../common/enums/FilterType";
import { ModuleType } from "../../../../common/enums/ModuleType";
import { TabModel } from "../../../../common/models/generic/TabModels/TabModel";
import { FilterConfig } from "../../../../common/models/workspace/FilterConfig";
import { RendererConfig } from "../../../../common/models/workspace/RendererConfig";
import { TabService } from "../../../../services/tabs/TabService";
import { firstValueFrom } from "rxjs";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/datamanagement/i18n/scope";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";

@Component({
    selector: "app-datamanagement-overview",
    templateUrl: "./datamanagement-overview.component.html",
    styleUrls: ["./datamanagement-overview.component.scss"],
    imports: [WorkspaceComponent],
    viewProviders: [provideTranslationScope(scope)],
})
export class DataManagementOverviewComponent implements OnInit {
    private _datamanagementDS = inject(DatamanagementDataService);
    private _tabs = inject(TabService);
    private _translation = injectTranslation(scope);
    pages = inject(Pages);
    private _permissions = inject(PermissionsService);

    moduleType = ModuleType.DM;
    tabID: string;
    tab: TabModel;
    data: Promise<any[]>;
    headerLookup: any = {};
    filterConfigs: FilterConfig[] = [];

    rendererConfig: RendererConfig[] = [
        {
            textColumn: "Most recent DPIA",
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
    ];

    numberProps: string[] = ["riskCount", "controlCount", "moiCount", "documentCount"];
    dateProps: string[] = ["Most recent DPIA"];

    permissions: WorkspaceModulePermissionModel;

    async ngOnInit() {
        this.permissions = this._permissions.permissions.Workspace.dataManagement;
        await this._translation.ready();
        const t = (key: string) => this._translation.t(key);
        this.headerLookup = {
            riskCount: t("riskCountHeader"),
            controlCount: t("controlCountHeader"),
            moiCount: t("moiCountHeader"),
            documentCount: t("documentCountHeader"),
            Identifier: t("identifierHeader"),
            Name: t("nameHeader"),
            Organization: t("organizationHeader"),
            OrganizationPath: t("organizationPathHeader"),
            Description: t("descriptionHeader"),
            Purpose: t("purposeHeader"),
            Basis: t("basisHeader"),
            "Explanation for the basis": t("explanationForTheBasisHeader"),
            "Business dimensions": t("businessDimensionsHeader"),
            "Framework dimensions": t("frameworkDimensionsHeader"),
            "Data subjects": t("dataSubjectsHeader"),
            "Data structures": t("dataStructuresHeader"),
            "Data Processors": t("dataProcessorsHeader"),
            "Data recipients": t("dataRecipientsHeader"),
            "Data controller": t("dataControllerHeader"),
            "Data transfer": t("dataTransferHeader"),
            "Data analytics": t("dataAnalyticsHeader"),
            "Source documents": t("sourceDocumentsHeader"),
            Delivery: t("deliveryHeader"),
            Removal: t("removalHeader"),
            "Security measures": t("securityMeasuresHeader"),
            "Sensitivity of Processing": t("sensitivityOfProcessingHeader"),
            "Transfer safeguard": t("transferSafeguardHeader"),
            "Third party name": t("thirdPartyNameHeader"),
            "Most recent DPIA": t("mostRecentDpiaHeader"),
            "DPIA reference": t("dpiaReferenceHeader"),
            "DPIA required": t("dpiaRequiredHeader"),
            "Latest conclusion DPIA": t("latestConclusionDpiaHeader"),
            "General Archive Policy": t("generalArchivePolicyHeader"),
            "Outside EU": t("outsideEuHeader"),
            Profiling: t("profilingHeader"),
        };
        this.filterConfigs = [
            {
                label: t("organizationsLabel"),
                column: "OrganizationID",
                type: FilterType.Organization,
                hideActionColumn: true,
                param: "",
            },
            {
                label: t("businessDimensionsLabel"),
                column: "BusinessDimensionIDs",
                type: FilterType.BusinessDimensions,
                hideActionColumn: true,
                param: "",
            },
            {
                label: t("frameworkDimensionsLabel"),
                column: "FrameworkDimensionIDs",
                type: FilterType.FrameworkDimensions,
                hideActionColumn: true,
                param: "",
            },
            {
                label: t("dataSubjectsLabel"),
                column: "DataSubjectIds",
                type: FilterType.DataSubjects,
                component: FilterComponent.MultiSelectTree,
                hideActionColumn: true,
                param: "",
            },
            {
                label: t("datastructuresLabel"),
                column: "DataStructureIds",
                type: FilterType.DataStructures,
                component: FilterComponent.MultiSelectTree,
                hideActionColumn: true,
                param: "",
            },
        ];
        this.data = firstValueFrom(this._datamanagementDS.getAll()).then(this.mapHeaders);
    }

    async openDetail(dm: any) {
        await this._translation.ready();
        this._tabs.generateTab(
            this.pages.DataManagement,
            dm.Guid,
            this._translation.t("openDetailTabTitle", {
                identifier: +dm.Identifier,
                name: dm.Name,
            })
        );
    }

    async add() {
        await this._translation.ready();
        this._tabs.generateTab(
            this.pages.DataManagement,
            null,
            this._translation.t("addNewTabTitle")
        );
    }

    private mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };
}
